import React, { FunctionComponent, useRef, useCallback, KeyboardEvent } from 'react';

import classnames from 'classnames';
import { Button, Col, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import InfiniteScroll from 'react-infinite-scroller';
import { trackPromise } from 'react-promise-tracker';

import { updateRemindMeLater } from 'admin/notifications/Notifications';
import {
    GroupNotificationHistory,
    NotificationHistory,
    NotificationHistoryStatus,
    NotificationType,
} from 'admin/notifications/Notifications.types';
import { useProfile } from 'auth/Auth';
import { getDesktopAlertLoader } from 'common/notification-tray/desktop-alert-registry/DesktopAlertRegistry';
import {
    getEffectiveEventType,
    getActionLabelKeyForNotification,
} from 'common/notification-tray/notification-tray-item/notification-utils';
import { ScrollHelper } from 'common/scroll-helper/ScrollHelper';
import { RelativeDateTime } from 'core/util/DateRange';
import 'common/notification-tray/notification-tray-item/NotificationTrayItem.scss';

interface Props {
    tab: string;
    handleStatePath?: (notification: NotificationHistory) => void;
    groupedNotificationHistory: GroupNotificationHistory;
    fetchAllNotificationHistories: (tab: string, start: number) => void;
    currentStart: number;
    hasMoreNotifications: boolean;
    handleNotificationCount?: (id: string, status: NotificationHistoryStatus) => void;
    handleRemindMeLater?: (notification: NotificationHistory) => Promise<NotificationHistory>;
    handleNotificationAction?: (notification: NotificationHistory) => void;
    handleRemoveNotification?: (notification: NotificationHistory) => void;
    refreshRemindMeLaterCount?: () => void;
}

const NOTIFICATION_TYPE_LABELS: Record<NotificationType, string> = {
    [NotificationType.Review3]: 'Review notification',
    [NotificationType.Review]: 'Review notification',
    [NotificationType.Listings]: 'Listing notification',
    [NotificationType.DataCollectionStatus]: 'Data collection status notification',
    [NotificationType.SocialPost]: '',
    [NotificationType.SocialReply]: '',
    [NotificationType.Ticket]: '',
    [NotificationType.ScheduledLocationPost]: '',
    [NotificationType.Mention]: '',
    [NotificationType.SurveyResult]: '',
    [NotificationType.SurveyResponse]: '',
    [NotificationType.SocialPostApproval]: '',
    [NotificationType.Summary]: '',
    [NotificationType.Survey1]: '',
    [NotificationType.ReviewResponseApproval]: '',
    [NotificationType.SurveyResponseApproval]: '',
    [NotificationType.InboxMessage]: '',
    [NotificationType.InboxConversation]: '',
    [NotificationType.InboxAssignment]: '',
    [NotificationType.Credential]: '',
    [NotificationType.Integration]: '',
    [NotificationType.OutReach]: '',
    [NotificationType.DuplicateProfiles]: '',
    [NotificationType.ProfileErrorListings]: '',
    [NotificationType.ProfileChangeListings]: '',
    [NotificationType.ListingVerification]: '',
    [NotificationType.CredentialMatching]: '',
    [NotificationType.CredentialSummary]: '',
    [NotificationType.TicketResponse]: '',
    [NotificationType.SocialNonAPIPost]: '',
    [NotificationType.SocialPostReplyResponseApproval]: '',
    [NotificationType.PublisherSuggestions]: '',
    [NotificationType.MessageResponseApproval]: '',
    [NotificationType.InboxConversationUpdate]: '',
    [NotificationType.ActionPlan]: '',
    [NotificationType.Review3ResponseApproval]: '',
    [NotificationType.ElasticReview]: 'DRAFT_RESPONSE',
    [NotificationType.FieldsMissingOnProfiles]: 'ACT_ON_ALL',
};

export const NotificationTrayItemProAct: FunctionComponent<Props> = ({
    tab,
    handleStatePath,
    groupedNotificationHistory,
    fetchAllNotificationHistories,
    currentStart,
    hasMoreNotifications,
    handleNotificationCount,
    handleRemindMeLater,
    handleNotificationAction,
    handleRemoveNotification,
    refreshRemindMeLaterCount,
}) => {
    const { t } = useTranslation('admin/notifications');
    const [{ profile }] = useProfile();
    const notificationRef = useRef<HTMLDivElement | null>(null);

    const isReadable = useCallback((type: NotificationType): boolean => {
        return type !== NotificationType.DataCollectionStatus;
    }, []);

    /** True only if this notification has a registered ProAct component. */
    const isRegisteredProAct = useCallback((notification: NotificationHistory): boolean => {
        const effectiveEventType = getEffectiveEventType(notification);

        return !!effectiveEventType && !!getDesktopAlertLoader(effectiveEventType);
    }, []);

    const getActionLabel = useCallback(
        (notification: NotificationHistory): string => {
            const labelKey = getActionLabelKeyForNotification(notification);

            return labelKey ? t(labelKey) : '';
        },
        [t],
    );

    const getNotificationTypeLabel = useCallback((type: NotificationType): string => {
        return NOTIFICATION_TYPE_LABELS[type] || 'Notification';
    }, []);

    const handleMarkRead = useCallback(
        (id: string, status: NotificationHistoryStatus, notification?: NotificationHistory): void => {
            if (notification && handleStatePath) {
                handleStatePath(notification);
            } else {
                handleNotificationCount?.(id, status);
            }
        },
        [handleStatePath, handleNotificationCount],
    );

    const remindMeLater = useCallback(
        async (notification: NotificationHistory): Promise<void> => {
            try {
                const notificationIDs = [notification.id];

                await trackPromise(
                    (async (): Promise<void> => {
                        await updateRemindMeLater(profile?.tenant?.id, notificationIDs);
                        await fetchAllNotificationHistories(tab, 0);
                        refreshRemindMeLaterCount?.();
                    })(),
                    'notifications-history',
                );
            } catch {
                // silently ignore
            }
        },
        [profile, fetchAllNotificationHistories, tab, refreshRemindMeLaterCount],
    );

    const handleNotificationClick = useCallback(
        (notification: NotificationHistory): void => {
            if (!isReadable(notification.type)) {
                return;
            }

            if (isRegisteredProAct(notification) && handleNotificationAction) {
                handleNotificationAction(notification);
            } else {
                handleMarkRead(notification.id, notification.status, notification);
            }
        },
        [isReadable, isRegisteredProAct, handleMarkRead, handleNotificationAction],
    );

    const handleNotificationKeyDown = useCallback(
        (event: KeyboardEvent<HTMLDivElement>, notification: NotificationHistory): void => {
            if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                handleNotificationClick(notification);
            }
        },
        [handleNotificationClick],
    );

    const getNotificationClasses = useCallback(
        (notification: NotificationHistory): string => {
            return classnames('notification-list-item', {
                'notification-nonactionable': !isReadable(notification.type),
                'notification-actionable': isReadable(notification.type),
                'notification-unread':
                    tab === 'all' &&
                    notification.status === NotificationHistoryStatus.UnRead &&
                    isReadable(notification.type),
            });
        },
        [tab, isReadable],
    );

    const getNotificationEmptyStateMessage = (): string => {
        if (tab === 'all') return t('NO_NOTIFICATIONS');
        if (tab === 'remindMeLater') return t('NO_REMIND_ME_LATER_NOTIFICATIONS');
        return t('NO_UNREAD_NOTIFICATIONS_TRAY');
    };

    const renderEmptyState = (): JSX.Element | null => {
        if (Object.keys(groupedNotificationHistory).length > 0) {
            return null;
        }
        return (
            <div className="no-notifications" role="status" aria-live="polite">
                <i className="icon-check-circle" aria-hidden="true" />
                <div>{getNotificationEmptyStateMessage()}</div>
            </div>
        );
    };

    const renderNotificationIcon = (notification: NotificationHistory): JSX.Element => (
        <Col>
            <div
                className="icon-container"
                aria-label={getNotificationTypeLabel(notification.eventType as unknown as NotificationType)}
                role="img"
            >
                <i className={notification.icon} aria-hidden="true" />
            </div>
        </Col>
    );

    const renderNotificationContent = (notification: NotificationHistory): JSX.Element => {
        const isUnread = notification.status === NotificationHistoryStatus.UnRead;

        return (
            <Col sm={10} className="pb-3">
                <div
                    className="notification-payload testclass"
                    // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={{ __html: notification.payload }}
                    aria-label={`${
                        isUnread ? t('UNREAD_NOTIFICATIONS') : t('READ_NOTIFICATIONS')
                    }${notification?.payload?.replace(/<[^>]*>/g, '')}`}
                />
            </Col>
        );
    };

    const renderNotificationArrow = (notification: NotificationHistory): JSX.Element => {
        return (
            <Col>
                {isReadable(notification.type) && (
                    <div aria-hidden="true">
                        {isRegisteredProAct(notification) ? (
                            <Button
                                variant="link"
                                className="notification-close-btn"
                                onClick={(e): void => {
                                    e.stopPropagation();
                                    handleRemoveNotification!(notification);
                                }}
                                title={t('CLOSE')}
                                aria-label={t('CLOSE')}
                            >
                                <i className="icon-times" aria-hidden="true" />
                            </Button>
                        ) : (
                            <i className="icon-angle-right" aria-hidden="true" />
                        )}
                    </div>
                )}
            </Col>
        );
    };

    const renderRemindButton = (notification: NotificationHistory): JSX.Element | null => {
        const remindedCount = notification.remindedCount || 0;

        // Hide both buttons once count reaches 3 (API fails on 3rd attempt)
        if (remindedCount >= 3) {
            return null;
        }

        const now = Date.now();
        const REMIND_WINDOW_MS = 24 * 60 * 60 * 1000; // 24 hours

        const isRemindedAndPending =
            notification.remindedFlag &&
            notification.remindedDateTime &&
            notification.remindedDateTime + REMIND_WINDOW_MS > now;

        if (isRemindedAndPending) {
            const remaining = notification.remindedDateTime + REMIND_WINDOW_MS - now;
            const ONE_HOUR = 60 * 60 * 1000;
            const remindHours = Math.max(1, Math.round(remaining / ONE_HOUR));

            return (
                <Button
                    variant="secondary"
                    disabled
                    title={t('REMINDS_IN_HOURS', { hours: remindHours })}
                    style={{
                        whiteSpace: 'nowrap',
                    }}
                >
                    {t('REMINDS_IN_HOURS', { hours: remindHours })}
                </Button>
            );
        }

        // Otherwise show clickable "Remind Me Later"
        return (
            <Button
                variant="secondary"
                onClick={(e): void => {
                    e.stopPropagation();
                    remindMeLater(notification);
                }}
                title={t('REMIND_ME_LATER')}
                style={{
                    whiteSpace: 'nowrap',
                }}
            >
                {t('REMIND_ME_LATER')}
            </Button>
        );
    };

    const renderNotificationActions = (notification: NotificationHistory): JSX.Element => {
        const actionLabel = getActionLabel(notification);

        return (
            <>
                <Col sm={1} />
                <Col sm={2}>
                    <div className="notification-metadata">
                        <span className="notification-time-ago">
                            <RelativeDateTime dateTime={notification.sentDate} addTimeAgo />
                        </span>
                        {/* Mark as read/unread intentionally removed when ProAct flag is enabled */}
                    </div>
                </Col>
                {isRegisteredProAct(notification) && (
                    <Col>
                        <Row>
                            <Col>{renderRemindButton(notification)}</Col>
                            <Col>
                                {actionLabel && (
                                    <Button
                                        variant="primary"
                                        onClick={(e): void => {
                                            e.stopPropagation();
                                            if (handleNotificationAction) {
                                                handleNotificationAction(notification);
                                            } else if (handleStatePath) {
                                                handleStatePath(notification);
                                            }
                                        }}
                                        title={actionLabel}
                                        style={{
                                            maxWidth: '140px',
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                        }}
                                    >
                                        {actionLabel}
                                    </Button>
                                )}
                            </Col>
                        </Row>
                    </Col>
                )}
            </>
        );
    };

    const renderNotification = (notification: NotificationHistory, index: number): JSX.Element => {
        const isUnread = notification.status === NotificationHistoryStatus.UnRead;
        const isActionable = isReadable(notification.type);

        return (
            <li key={notification.id.toString()}>
                <div
                    role="button"
                    tabIndex={isActionable ? 0 : -1}
                    onKeyDown={(e): void => handleNotificationKeyDown(e, notification)}
                    onClick={(): void => handleNotificationClick(notification)}
                    className={getNotificationClasses(notification)}
                    aria-pressed={isActionable && !isUnread ? 'true' : undefined}
                >
                    <Row noGutters>
                        {renderNotificationIcon(notification)}
                        {renderNotificationContent(notification)}
                        {renderNotificationArrow(notification)}
                    </Row>
                    <Row noGutters>{renderNotificationActions(notification)}</Row>
                </div>
            </li>
        );
    };

    const renderNotificationGroup = (key: string): JSX.Element => {
        const notifications = groupedNotificationHistory[key];

        /**
         * Duplicate notificatoins issue
         */
        const uniqueNotifications = Array.from(new Map(notifications.map((n) => [n.id, n])).values());

        return (
            <React.Fragment key={key}>
                <li className="notification-group-by" role="presentation">
                    <div className="notification-group-name" role="heading" aria-level={2}>
                        {t(key)}
                    </div>
                </li>
                {uniqueNotifications.map((notification, index) => renderNotification(notification, index))}
            </React.Fragment>
        );
    };

    const totalNotifications = Object.values(groupedNotificationHistory).reduce(
        (sum, notifications) => sum + notifications.length,
        0,
    );

    return (
        <div className="r4e-notification-tray-item" ref={notificationRef} role="region">
            <ScrollHelper position="top" direction="top" parent={notificationRef} />
            <InfiniteScroll
                pageStart={0}
                initialLoad={false}
                loadMore={(): void => fetchAllNotificationHistories(tab, currentStart)}
                hasMore={hasMoreNotifications}
                useWindow={false}
            >
                {renderEmptyState()}
                <ul
                    className="notification-list"
                    aria-label={`${totalNotifications} notification${totalNotifications !== 1 ? 's' : ''}`}
                >
                    {Object.keys(groupedNotificationHistory).map(renderNotificationGroup)}
                </ul>
            </InfiniteScroll>
        </div>
    );
};
